import type { ReactElement } from 'react';
import { EditableText } from '../../../components/Editable';
import { contentPatchFor, resolveNodeText, selectionFieldFor } from '../content';
import { StudioBox, toneColor, type StudioNodeRendererProps } from './box';

/**
 * All seven typography primitives share one shape: a click-to-edit text node
 * bound via `contentRef` (see `content.ts`), sized by the resolver's
 * `fontSize` (inherited from `StudioBox`'s inline style — `studio.css`
 * overrides `Editable`'s class-based font-size to `inherit` inside a studio
 * node so the resolved size always wins, never the archetype default).
 */
function TextNode({ node, ctx, placeholder, markdown }: StudioNodeRendererProps & { placeholder: string; markdown?: boolean }): ReactElement {
  const { section, actions, readOnly } = ctx;
  const contentRef = node.props?.contentRef;
  const value = resolveNodeText(section, node.props);
  const color = toneColor(node.props?.tone);
  return (
    <StudioBox node={node} style={color ? { color } : undefined}>
      <EditableText
        value={value}
        field={selectionFieldFor(contentRef)}
        placeholder={placeholder}
        readOnly={readOnly}
        markdown={markdown}
        onSave={(next) => actions.saveContent(section, contentPatchFor(section, contentRef, next))}
      />
    </StudioBox>
  );
}

export function EyebrowNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Eyebrow" />;
}

export function HeadlineNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Headline" />;
}

export function SubheadlineNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Subheadline" />;
}

export function BodyNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Body copy" markdown />;
}

export function CaptionNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Caption" />;
}

export function LabelNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Label" />;
}

export function QuoteNode(props: StudioNodeRendererProps): ReactElement {
  return <TextNode {...props} placeholder="Quote" />;
}
